/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext } from 'react'
import {
  Heading6,
  Box,
  Color,
  BorderStyle,
  Space,
  Icon,
  Text,
  JustifyContent,
  FontWeight,
  AlignItems,
  IconVariant,
  LinkVariant,
  Link,
  Join,
  IconNames,
} from '@gousto-internal/citrus-react'
import { getUserID } from 'src/selectors/user'
import { store } from 'src/store'
import { OrderDetails } from '../OrderDetails'
import { externalGoustoPages } from '../../config/routes'
import { AddTrackingToChild } from '../Tracking'
import type { HTMLTrackingAttributes } from '../Tracking'
import { TrackingAction } from '../../common/tracking'

interface OrderDetailsProps {
  order: OrderDetailsType,
}

type OrderDetailsType = {
  id: string,
  deliveryDate: string,
  price: string,
  state: string,
  images: { alt: string, src: string }[]
}

export enum CircularIconStyle {
  INGREDIENTS = 'ingredients',
  DELIVERY = 'delivery',
  RECIPE = 'recipe',
}

type CircularIconProps = {
  iconStyle: CircularIconStyle;
}

type CircularIconGroupedStyles = Record<CircularIconStyle, {
  iconColor: IconVariant,
  bg: Color,
  iconName: IconNames,
}>

const circularIconGroupedStyles: CircularIconGroupedStyles = {
  [CircularIconStyle.INGREDIENTS]: {
    iconColor: IconVariant.Error,
    bg: Color.Primary_50,
    iconName: '5_a_day',
  },
  [CircularIconStyle.DELIVERY]: {
    iconColor: IconVariant.Actionable,
    bg: Color.Secondary_50,
    iconName: 'delivery',
  },
  [CircularIconStyle.RECIPE]: {
    iconColor: IconVariant.Confirmation,
    bg: Color.Success_50,
    iconName: 'menu_book',
  },
}

const CircularIcon = ({ iconStyle }: CircularIconProps) => {
  const { iconColor, bg, iconName } = circularIconGroupedStyles[iconStyle]

  return (
    <Box
      bg={bg}
      borderRadius="round"
      paddingH={3}
      paddingV={3}
      display="inline-flex"
      width="auto"
      height="auto"
    >
      <Icon size={6} name={iconName} variant={iconColor} />
    </Box>
  )
}

type HelpCentreEntryNavigationLineProps = HTMLTrackingAttributes & {
  iconStyle: CircularIconStyle
  title: string,
  description: string,
  href: string,
}

const HelpCentreEntryNavigationLine = (
  {
    iconStyle, title, description, href, ...trackingAtributes
  }: HelpCentreEntryNavigationLineProps,
) => (
  <Link
    style={{ display: 'block', textDecoration: 'none' }}
    href={href}
    variant={LinkVariant.None}
    {...trackingAtributes}
  >
    <Box
      paddingH={3}
      paddingV={3}
      display="flex"
      justifyContent={JustifyContent.SpaceBetween}
    >
      <Box alignItems={AlignItems.Center} display="flex">
        <Box paddingRight={3}>
          <CircularIcon iconStyle={iconStyle} />
        </Box>
        <Box>
          <Text fontWeight={FontWeight.SemiBold} size={2}>
            {title}
          </Text>
          <Text size={1}>
            {description}
          </Text>
        </Box>
      </Box>
      <Box alignItems={AlignItems.Center} display="flex">
        <Icon name="chevron_right" variant={IconVariant.Actionable} />
      </Box>
    </Box>
  </Link>
)

const OrderCard = ({ order }: OrderDetailsProps) => {
  const { state } = useContext(store)
  const userId = getUserID(state)
  if (!order) return null
  const {
    deliveryDate,
    price,
    images,
  } = order

  const createTrackingProperties = (issue: string) => ({
    issue,
    order_id: order.id,
    user_id: userId,
  })

  return (
    <Box>
      <Box
        paddingH={[3, 0]}
      >
        <Heading6>I need help with my last order</Heading6>
      </Box>
      <Space size={2} />
      <Box
        borderStyle={BorderStyle.Solid}
        borderColor={Color.ColdGrey_100}
        borderWidth={0.5}
        paddingH={3}
        paddingV={3}
        bg={Color.White}
      >
        <OrderDetails
          deliveryDate={deliveryDate}
          orderState={order.state}
          price={price}
          recipeImages={images}
        />
        <Join
          with={(
            <Box
              borderStyle={BorderStyle.Solid}
              borderColor={Color.ColdGrey_100}
              borderBottomWidth={0.5}
              height="1px"
            />
          )}
        >
          <AddTrackingToChild
            dataTrackingAction={TrackingAction.SSROrderIssueSelected}
            dataTrackingProperty={createTrackingProperties('ingredients')}
          >
            <HelpCentreEntryNavigationLine
              href={externalGoustoPages.ssrIngredients({ userId, orderId: order.id })}
              iconStyle={CircularIconStyle.INGREDIENTS}
              title="Ingredients"
              description="Missing, damaged, wrong or quality"
            />
          </AddTrackingToChild>
          <AddTrackingToChild
            dataTrackingAction={TrackingAction.SSROrderIssueSelected}
            dataTrackingProperty={createTrackingProperties('delivery')}
          >
            <HelpCentreEntryNavigationLine
              href={externalGoustoPages.ssrDelivery({ userId, orderId: order.id })}
              iconStyle={CircularIconStyle.DELIVERY}
              title="Deliveries"
              description="Where is my box or it didn't arrive"
            />
          </AddTrackingToChild>
          <AddTrackingToChild
            dataTrackingAction={TrackingAction.SSROrderIssueSelected}
            dataTrackingProperty={createTrackingProperties('recipe_card')}
          >
            <HelpCentreEntryNavigationLine
              href={externalGoustoPages.ssrRecipeCards({ userId, orderId: order.id })}
              iconStyle={CircularIconStyle.RECIPE}
              title="Recipe Cards"
              description="Missing or wrong"
            />
          </AddTrackingToChild>
        </Join>
      </Box>
    </Box>
  )
}

export { OrderCard }
